import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Mail,
  Upload,
  Zap,
  FileText,
  CheckCircle2,
  Clock,
  Loader2,
  ArrowRight,
  Eye,
} from 'lucide-react'
import { INCOMING_DOCUMENTS as DEMO_INCOMING_DOCUMENTS } from '@/data/documentIngestionMock'
import type { IncomingDocument, IngestionStatus } from '@/types/documentIngestion'
import { demoEnabled } from '@/config/demoMode'
import { ingestionService } from '@/services/ingestion'
import { formatDeskDate, formatDeskTime } from '@/utils/dates'
import { intakeRecordsToDocuments } from './intakeDocuments'

const STATUS_CONFIG: Record<IngestionStatus, { color: string; label: string; icon: React.ReactNode }> = {
  received: { color: 'text-blue-400 bg-blue-400/10 border-blue-400/20', label: 'Received', icon: <Mail size={10} /> },
  parsing: { color: 'text-amber-400 bg-amber-400/10 border-amber-400/20', label: 'Parsing', icon: <Loader2 size={10} className="animate-spin" /> },
  extracted: { color: 'text-purple-400 bg-purple-400/10 border-purple-400/20', label: 'Extracted', icon: <Zap size={10} /> },
  validated: { color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', label: 'Validated', icon: <CheckCircle2 size={10} /> },
  filed: { color: 'text-stone-400 bg-stone-400/10 border-stone-400/20', label: 'Filed', icon: <FileText size={10} /> },
}

const SOURCE_ICON: Record<string, React.ReactNode> = {
  email: <Mail size={12} className="text-blue-400" />,
  upload: <Upload size={12} className="text-amber-400" />,
  api: <Zap size={12} className="text-purple-400" />,
}

// The desk's clock (New York), not the viewer's: the same arrival reads the same everywhere.
function fmtDate(d: string) {
  const day = formatDeskDate(d, 'short')
  const time = formatDeskTime(d)
  return time ? `${day} ${time}` : day || '—'
}

export default function DocumentIngestion() {
  const navigate = useNavigate()
  const demo = demoEnabled()
  // Live accounts read the documents intake actually received (stress test
  // MOD-20: the only source used to be the demo fixture, so this read zero
  // beside a Vault of filed intake documents). Demo keeps its fixture.
  const live = useQuery({
    queryKey: ['ingestion-documents'],
    queryFn: () => ingestionService.getAll(),
    enabled: !demo,
    refetchInterval: 60_000,
  })
  const INCOMING_DOCUMENTS: IncomingDocument[] = useMemo(
    () => (demo ? DEMO_INCOMING_DOCUMENTS : intakeRecordsToDocuments(live.data ?? [])),
    [demo, live.data],
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selectedDoc = INCOMING_DOCUMENTS.find((d) => d.id === selectedId) ?? INCOMING_DOCUMENTS[0] ?? null
  const setSelectedDoc = (doc: IncomingDocument) => setSelectedId(doc.id)

  // Pipeline counts
  const pipeline = {
    received: INCOMING_DOCUMENTS.filter((d) => d.status === 'received').length,
    parsing: INCOMING_DOCUMENTS.filter((d) => d.status === 'parsing').length,
    extracted: INCOMING_DOCUMENTS.filter((d) => d.status === 'extracted').length,
    validated: INCOMING_DOCUMENTS.filter((d) => d.status === 'validated').length,
    filed: INCOMING_DOCUMENTS.filter((d) => d.status === 'filed').length,
  }

  return (
    <div className="space-y-3">
      {/* Pipeline Status */}
      <div className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 p-3">
        {(Object.entries(pipeline) as [IngestionStatus, number][]).map(([status, count], i) => {
          const cfg = STATUS_CONFIG[status]
          return (
            <div key={status} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium ${cfg.color}`}>
                {cfg.icon}
                {cfg.label}
                <span className="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold">{count}</span>
              </div>
              {i < 4 && <ArrowRight size={12} className="text-stone-700" />}
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-5 gap-3">
        {/* Document List */}
        <div className="col-span-3 panel">
          <div className="panel-header">
            <span className="panel-header-title">Incoming Documents</span>
            <span className="text-[10px] font-mono text-stone-600">{INCOMING_DOCUMENTS.length} items</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Deal</th>
                  <th>Status</th>
                  <th className="text-right" title="Share of core fields the engine read — completeness, not a model's confidence">Coverage</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {!demo && live.isLoading && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-[11px] text-stone-500">
                      Loading the documents intake received…
                    </td>
                  </tr>
                )}
                {!demo && live.isError && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-[11px] text-red-300">
                      The intake records could not be read, so this list is unavailable. The Vault is unaffected.
                    </td>
                  </tr>
                )}
                {INCOMING_DOCUMENTS.length === 0 && !live.isLoading && !live.isError && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-[11px] text-stone-500">
                      No documents in the ingestion pipeline yet. Forward an email or upload a file and it will appear here for parsing and extraction.
                    </td>
                  </tr>
                )}
                {INCOMING_DOCUMENTS.map((doc) => {
                  const cfg = STATUS_CONFIG[doc.status]
                  return (
                    <tr
                      key={doc.id}
                      onClick={() => setSelectedDoc(doc)}
                      className={`cursor-pointer ${selectedDoc?.id === doc.id ? 'selected' : ''}`}
                    >
                      <td>{SOURCE_ICON[doc.source]}</td>
                      <td>
                        <div className="max-w-[200px]">
                          <div className="text-stone-200 text-[11px] font-medium truncate">{doc.fileName}</div>
                          {doc.senderName && (
                            <div className="text-[9px] text-stone-600 truncate">{doc.senderName}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        {doc.documentType ? (
                          <span className="text-[10px] rounded bg-stone-800 px-1.5 py-0.5 text-stone-300">{doc.documentType}</span>
                        ) : (
                          <span className="text-[10px] text-stone-600 italic">Unclassified</span>
                        )}
                      </td>
                      <td className="font-mono text-[10px] text-stone-400">{doc.dealId ?? '—'}</td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border ${cfg.color}`}>
                          {cfg.icon} {cfg.label}
                        </span>
                      </td>
                      <td className="text-right">
                        {doc.confidence != null ? (
                          <span className={`text-[11px] font-mono ${doc.confidence >= 0.90 ? 'text-emerald-400' : doc.confidence >= 0.80 ? 'text-amber-400' : 'text-red-400'}`}>
                            {(doc.confidence * 100).toFixed(0)}%
                          </span>
                        ) : '—'}
                      </td>
                      <td className="text-[10px] text-stone-500 whitespace-nowrap">{fmtDate(doc.receivedAt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Panel */}
        <div className="col-span-2 panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-1.5">
              <Eye size={12} />
              Document Detail
            </span>
          </div>
          <div className="panel-body">
            {selectedDoc ? (
              <div className="space-y-4">
                <div>
                  <div className="text-stone-200 text-sm font-semibold">{selectedDoc.fileName}</div>
                  <div className="text-[10px] text-stone-500 mt-0.5">
                    {selectedDoc.fileSize} &middot; {selectedDoc.fileType.toUpperCase()}
                    {selectedDoc.senderEmail && <> &middot; {selectedDoc.senderEmail}</>}
                  </div>
                </div>

                {selectedDoc.subject && (
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-1">Subject</div>
                    <div className="text-[11px] text-stone-300">{selectedDoc.subject}</div>
                  </div>
                )}

                <div className="gold-divider" />

                {/* Extracted Fields */}
                {selectedDoc.extractedFields && (
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600 mb-2">Extracted Data</div>
                    <div className="space-y-1.5">
                      {Object.entries(selectedDoc.extractedFields).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between rounded border border-stone-700/30 bg-stone-900/30 px-2.5 py-1.5">
                          <span className="text-[11px] text-stone-400">{key}</span>
                          <span className="text-[11px] font-mono text-stone-200 font-medium">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!selectedDoc.extractedFields && (
                  <div className="flex flex-col items-center py-6 text-stone-600">
                    <Clock size={20} className="mb-2" />
                    <p className="text-[11px]">Awaiting extraction</p>
                  </div>
                )}

                {/* Actions. A live document is reviewed, filed and attached to
                    its deal on the intake record it arrived with — this opens
                    it there rather than offering buttons that do nothing. */}
                {selectedDoc.ingestionId ? (
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/intake?focus=${encodeURIComponent(selectedDoc.ingestionId!)}`)}
                      className="flex-1 rounded border border-amber-400/20 bg-amber-400/[0.06] px-3 py-1.5 text-[10px] font-medium text-amber-300 hover:bg-amber-400/10 transition-colors"
                    >
                      Open in Intake
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-stone-600">
                <FileText size={20} className="mb-2" />
                <p className="text-[11px]">Select a document to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
